/**
 * EquipmentTypeWebshopTab — STORY-1306 slice C (proto #13 webshop-arc).
 *
 * Mockup (02-type-editor TAB 1): 3 visibility-flag + 4-locale nyelvi switcher
 * (HU* · EN/DE/UK opcionális, üres → HU-fallback a megjelenítésben) + slug
 * (HU-nézeten szerkeszthető + autogen a HU névből, `/shop/` preview).
 *
 * Kontraktus: #407 (`equipmentTypeWebshopReadSchema`/`PatchSchema`) — a
 * fragment-read + patch a `use-equipment-type-webshop` hookon át, runtime
 * Zod-validálással.
 *
 * DIVERGENCIA-NAPLÓ: a HU-név kötelezősége webshop-láthatóságnál itt SOFT
 * figyelmeztetés (a kontraktus nem enforce-olja; BE-szabály ha lesz, Zeno-lane).
 * A mockup „Kép kötelező" tooltip-szabálya szintén nem kontraktus-szintű még.
 */

import { Button, Card, CardContent } from '@/components/ui';
import { useEquipmentTypeWebshop } from '@/hooks/use-equipment-type-webshop';
import { useToast } from '@/hooks/use-toast';
import type { EquipmentTypeWebshopPatch, WebshopTexts } from '@kgc/equipment-type-domain';
import { useTranslation } from '@kgc/i18n';
import { useEffect, useState } from 'react';

const LOCALES = ['hu', 'en', 'de', 'uk'] as const;
type LocaleCode = (typeof LOCALES)[number];

type LocaleText = { name: string; description: string };
type TextsState = Record<LocaleCode, LocaleText>;

const EMPTY_TEXTS: TextsState = {
  hu: { name: '', description: '' },
  en: { name: '', description: '' },
  de: { name: '', description: '' },
  uk: { name: '', description: '' },
};

/**
 * SEO placeholder-váz (b) — per-locale `seoTitle`/`seoDescription`. SZÁNDÉKOSAN
 * KÜLÖN state, NEM a kontraktus-`texts`-ben: a `EquipmentTypeWebshopPatch` (DTO)
 * jelenleg NEM hordoz SEO-mezőt, így a save-be NEM kerül (placeholder-invariáns).
 * A BE-Zod-seam (webshopTexts-shape bővítés) köti be utólag (Q-C010 return-minta,
 * Javo (b) ratify 2026-06-16). A különtartás miatt a save-logika + a 7 meglévő
 * teszt érintetlen marad, a seam-bekötés majd minimális diff (hydrate + patch).
 */
type LocaleSeo = { seoTitle: string; seoDescription: string };
type SeoTextsState = Record<LocaleCode, LocaleSeo>;

const EMPTY_SEO: SeoTextsState = {
  hu: { seoTitle: '', seoDescription: '' },
  en: { seoTitle: '', seoDescription: '' },
  de: { seoTitle: '', seoDescription: '' },
  uk: { seoTitle: '', seoDescription: '' },
};

// SEO-hossz guidance (Google SERP-közeli): cél / kemény-max → char-count szín + badge.
const SEO_TITLE_IDEAL = 60;
const SEO_TITLE_MAX = 70;
const SEO_DESC_IDEAL = 160;
const SEO_DESC_MAX = 200;

/** Char-count szín: üres → muted · ≤ideal → zöld · ≤hardMax → sárga · fölötte → piros. */
function seoCountColor(len: number, ideal: number, hardMax: number): string {
  if (len === 0) return 'text-muted-foreground';
  if (len <= ideal) return 'text-emerald-600 dark:text-emerald-400';
  if (len <= hardMax) return 'text-amber-600 dark:text-amber-400';
  return 'text-rose-600 dark:text-rose-400';
}

/** Ékezet-strip + kisbetű + nem-alfanumerikus → kötőjel (mockup autoSlug). */
function slugify(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** A state → kontraktus `webshopTexts` (üres locale-ok kimaradnak; 0 locale → null). */
function textsToPatch(texts: TextsState): WebshopTexts | null {
  const out: WebshopTexts = {};
  for (const loc of LOCALES) {
    const t = texts[loc];
    if (t.name.trim().length > 0 || t.description.trim().length > 0) {
      out[loc] = { name: t.name, description: t.description };
    }
  }
  return Object.keys(out).length > 0 ? out : null;
}

interface EquipmentTypeWebshopTabProps {
  typeId: string;
}

export function EquipmentTypeWebshopTab({ typeId }: EquipmentTypeWebshopTabProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { data, isLoading, isError, save, isSaving } = useEquipmentTypeWebshop(typeId);

  const [activeLang, setActiveLang] = useState<LocaleCode>('hu');
  const [texts, setTexts] = useState<TextsState>(EMPTY_TEXTS);
  // SEO placeholder-váz — külön a kontraktus-texts-től (a save NEM küldi, lásd EMPTY_SEO doc).
  const [seoTexts, setSeoTexts] = useState<SeoTextsState>(EMPTY_SEO);
  const [slug, setSlug] = useState('');
  const [flags, setFlags] = useState({
    visibleOnWebshop: false,
    availableForRent: false,
    availableForSale: false,
  });
  const [saveError, setSaveError] = useState<string | null>(null);

  // Hydrate a fragment-ből (a hook-mock STABIL identitású — #12 spec-tanulság)
  useEffect(() => {
    if (!data) return;
    const hydrated: TextsState = { ...EMPTY_TEXTS };
    for (const loc of LOCALES) {
      const src = data.webshopTexts?.[loc];
      hydrated[loc] = { name: src?.name ?? '', description: src?.description ?? '' };
    }
    setTexts(hydrated);
    setSlug(data.slug ?? '');
    setFlags({
      visibleOnWebshop: data.visibleOnWebshop,
      availableForRent: data.availableForRent,
      availableForSale: data.availableForSale,
    });
  }, [data]);

  if (isLoading) {
    return (
      <p data-testid="webshop-tab-loading" className="py-6 text-sm text-muted-foreground">
        {t('equipment.types.editor.webshop.loading')}
      </p>
    );
  }
  if (isError || !data) {
    return (
      <p data-testid="webshop-tab-error" className="py-6 text-sm text-rose-600 dark:text-rose-400">
        {t('equipment.types.editor.webshop.error')}
      </p>
    );
  }

  const active = texts[activeLang];
  const huNameEmpty = texts.hu.name.trim().length === 0;

  const updateActive = (patch: Partial<LocaleText>) => {
    setTexts(prev => ({ ...prev, [activeLang]: { ...prev[activeLang], ...patch } }));
  };

  const activeSeo = seoTexts[activeLang];
  const updateActiveSeo = (patch: Partial<LocaleSeo>) => {
    setSeoTexts(prev => ({ ...prev, [activeLang]: { ...prev[activeLang], ...patch } }));
  };

  /** Egy locale kitöltöttsége a pill-statusdothoz: none / partial / full. */
  const localeStatus = (loc: LocaleCode): 'none' | 'partial' | 'full' => {
    const lt = texts[loc];
    const filled = [lt.name, lt.description].filter(v => v.trim().length > 0).length;
    return filled === 0 ? 'none' : filled === 2 ? 'full' : 'partial';
  };

  const handleSave = async () => {
    setSaveError(null);
    const patch: EquipmentTypeWebshopPatch = {
      slug: slug.trim().length > 0 ? slug.trim() : null,
      webshopTexts: textsToPatch(texts),
      visibleOnWebshop: flags.visibleOnWebshop,
      availableForRent: flags.availableForRent,
      availableForSale: flags.availableForSale,
    };
    try {
      await save(patch);
      toast({ title: t('equipment.types.editor.webshop.saved') });
    } catch {
      setSaveError(t('equipment.types.editor.webshop.saveFailed'));
    }
  };

  const flagDefs = [
    { key: 'visibleOnWebshop', label: 'flagVisibleOnWebshop', hint: 'flagVisibleOnWebshopHint' },
    { key: 'availableForRent', label: 'flagAvailableForRent', hint: 'flagAvailableForRentHint' },
    { key: 'availableForSale', label: 'flagAvailableForSale', hint: 'flagAvailableForSaleHint' },
  ] as const;

  return (
    <div className="space-y-4">
      {/* 3 visibility-flag */}
      <Card>
        <CardContent className="p-5 space-y-3">
          {flagDefs.map(def => (
            <label key={def.key} className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={flags[def.key]}
                onChange={e => setFlags(prev => ({ ...prev, [def.key]: e.target.checked }))}
                className="mt-0.5 h-4 w-4 rounded"
                data-testid={`webshop-flag-${def.key}`}
              />
              <span>
                <span className="block text-sm font-medium text-foreground">
                  {t(`equipment.types.editor.webshop.${def.label}`)}
                </span>
                <span className="block text-xs text-muted-foreground">
                  {t(`equipment.types.editor.webshop.${def.hint}`)}
                </span>
              </span>
            </label>
          ))}
        </CardContent>
      </Card>

      {/* Nyelvi tartalom + slug */}
      <Card>
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h3 className="text-sm font-semibold text-foreground">
              {t('equipment.types.editor.webshop.localeTitle')}
            </h3>
            <p className="text-[11px] text-muted-foreground">
              {t('equipment.types.editor.webshop.localeHint')}
            </p>
          </div>

          {/* Locale-pill-ek (HU* + fill-status dot) */}
          <div className="inline-flex rounded-lg bg-muted p-1 gap-1">
            {LOCALES.map(loc => {
              const status = localeStatus(loc);
              return (
                <button
                  key={loc}
                  type="button"
                  onClick={() => setActiveLang(loc)}
                  data-testid={`webshop-lang-${loc}`}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                    activeLang === loc
                      ? 'bg-white dark:bg-gray-900 text-primary shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {loc.toUpperCase()}
                  {loc === 'hu' && <span className="text-rose-600 dark:text-rose-400">*</span>}
                  {loc !== 'hu' && status === 'partial' && (
                    <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                  )}
                  {loc !== 'hu' && status === 'full' && (
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  )}
                </button>
              );
            })}
          </div>

          {/* Név */}
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
              {t('equipment.types.editor.webshop.nameLabel')}
              {activeLang === 'hu' && <span className="text-rose-600 dark:text-rose-400"> *</span>}
            </label>
            <input
              type="text"
              value={active.name}
              onChange={e => updateActive({ name: e.target.value })}
              placeholder={
                activeLang === 'hu'
                  ? t('equipment.types.editor.webshop.namePlaceholderHu')
                  : texts.hu.name
              }
              className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
              data-testid="webshop-name-input"
            />
            {huNameEmpty && flags.visibleOnWebshop && (
              <p
                data-testid="webshop-hu-name-warning"
                className="mt-1 text-[11px] text-rose-600 dark:text-rose-400"
              >
                {t('equipment.types.editor.webshop.huNameRequired')}
              </p>
            )}
          </div>

          {/* Leírás */}
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
              {t('equipment.types.editor.webshop.descriptionLabel')}
            </label>
            <textarea
              value={active.description}
              onChange={e => updateActive({ description: e.target.value })}
              rows={4}
              className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
              data-testid="webshop-description-input"
            />
          </div>

          {/* Slug — HU-nézeten szerkeszthető (mockup), autogen a HU névből */}
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
              {t('equipment.types.editor.webshop.slugLabel')}
              <span className="ml-2 font-mono normal-case text-muted-foreground">
                /shop/{slug || '…'}
              </span>
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={slug}
                readOnly={activeLang !== 'hu'}
                onChange={e => setSlug(e.target.value)}
                placeholder="honda-gx160"
                className="flex-1 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 font-mono text-sm read-only:opacity-60"
                data-testid="webshop-slug-input"
              />
              {activeLang === 'hu' && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setSlug(slugify(texts.hu.name))}
                  data-testid="webshop-slug-generate"
                >
                  {t('equipment.types.editor.webshop.slugGenerate')}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* SEO placeholder-váz (b) — per-locale, a save-bekötés a BE-Zod-seamre vár */}
      <Card>
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h3 className="text-sm font-semibold text-foreground">
              {t('equipment.types.editor.webshop.seoSectionTitle')}
            </h3>
            <span className="text-[11px] font-mono text-muted-foreground">
              {activeLang.toUpperCase()}
            </span>
          </div>

          {/* Placeholder-jelzés: a SEO-mezők még NEM perzisztálnak (kontraktus előkészítés alatt) */}
          <p
            data-testid="webshop-seo-placeholder-note"
            className="rounded-md bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900 px-3 py-2 text-[11px] text-amber-800 dark:text-amber-300"
          >
            {t('equipment.types.editor.webshop.seoPlaceholderNote')}
          </p>

          {/* SEO cím */}
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1 flex items-center justify-between">
              <span>{t('equipment.types.editor.webshop.seoTitleLabel')}</span>
              <span
                data-testid="webshop-seo-title-count"
                className={`font-normal normal-case text-[11px] ${seoCountColor(
                  activeSeo.seoTitle.length,
                  SEO_TITLE_IDEAL,
                  SEO_TITLE_MAX
                )}`}
              >
                {activeSeo.seoTitle.length} / {SEO_TITLE_IDEAL}
                {activeSeo.seoTitle.length > SEO_TITLE_MAX && (
                  <span className="ml-1 font-bold">
                    ⚠ {t('equipment.types.editor.webshop.seoTooLong')}
                  </span>
                )}
              </span>
            </label>
            <input
              type="text"
              value={activeSeo.seoTitle}
              onChange={e => updateActiveSeo({ seoTitle: e.target.value })}
              placeholder={t('equipment.types.editor.webshop.seoTitlePlaceholder')}
              className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
              data-testid="webshop-seo-title-input"
            />
          </div>

          {/* SEO leírás */}
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1 flex items-center justify-between">
              <span>{t('equipment.types.editor.webshop.seoDescriptionLabel')}</span>
              <span
                data-testid="webshop-seo-desc-count"
                className={`font-normal normal-case text-[11px] ${seoCountColor(
                  activeSeo.seoDescription.length,
                  SEO_DESC_IDEAL,
                  SEO_DESC_MAX
                )}`}
              >
                {activeSeo.seoDescription.length} / {SEO_DESC_IDEAL}
                {activeSeo.seoDescription.length > SEO_DESC_MAX && (
                  <span className="ml-1 font-bold">
                    ⚠ {t('equipment.types.editor.webshop.seoTooLong')}
                  </span>
                )}
              </span>
            </label>
            <textarea
              value={activeSeo.seoDescription}
              onChange={e => updateActiveSeo({ seoDescription: e.target.value })}
              rows={2}
              placeholder={t('equipment.types.editor.webshop.seoDescriptionPlaceholder')}
              className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
              data-testid="webshop-seo-desc-input"
            />
          </div>
        </CardContent>
      </Card>

      {saveError && (
        <p data-testid="webshop-save-error" className="text-sm text-rose-600 dark:text-rose-400">
          {saveError}
        </p>
      )}

      <div className="flex justify-end">
        <Button
          type="button"
          onClick={() => void handleSave()}
          disabled={isSaving}
          data-testid="webshop-save"
        >
          {isSaving
            ? t('equipment.types.editor.webshop.saving')
            : t('equipment.types.editor.webshop.save')}
        </Button>
      </div>
    </div>
  );
}
